import CircularProgress from '@material-ui/core/CircularProgress'
import React, { memo, useCallback } from 'react'
import { useTreeViewStyles } from '../../styles/useTreeViewStyles'

import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import VisibilityOffSharpIcon from '@material-ui/icons/VisibilityOffSharp'
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'
import VisibilitySharpIcon from '@material-ui/icons/VisibilitySharp'
import { tryGetShortcutKey } from 'components/WorkspaceEditor/utils/shortcutKeyUtils'

/** Props for rendering visibility icon of <TreeView /> component */
export interface VisibilityIconProps {
    /** To check if allowed to use visibility icon */
    contentTeamFromStudioPage: boolean
    /** To check if visibility icon is disabled */
    disabled: boolean
    /* A flag whether the node is loading */
    isLoading: boolean
    /* A flag whether the node is expanded */
    isHidden: boolean
    'data-testid'?: string
    /* A utility  funtion to deactivate/activate property */
    onClickIcon: (isHidden: boolean) => void
    /* A flag whether the node is article */
    isArticle?: boolean
    /** Forces a specific tabIndex (e.g. -1 while the hover bar is collapsed) */
    tabIndexOverride?: number
}

/**
 * Renders the visibility icon property of <TreeView /> component
 * @param {VisibilityIconProps} props - Props for rendering visibility icon
 * @returns {ReactElement} Node visibility icon
 */
export const VisibilityIcon = memo<VisibilityIconProps>(
    ({
        contentTeamFromStudioPage,
        isLoading,
        isHidden,
        disabled,
        onClickIcon,
        isArticle,
        tabIndexOverride,
        'data-testid': dataTestId = 'visibility-icon',
    }) => {
        const classes = useTreeViewStyles()

        const handleClick = useCallback(() => {
            onClickIcon(!isHidden)
        }, [onClickIcon, isHidden])

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent) => {
                const enterKey = tryGetShortcutKey('Enter', e, true)
                const spaceKey = tryGetShortcutKey('Space', e, true)

                if (enterKey || spaceKey) {
                    e.preventDefault()
                    handleClick()
                }
            },
            [handleClick]
        )

        if (isLoading) {
            return (
                <CircularProgress
                    data-testid={dataTestId}
                    className={classes.switcherLoadingIcon}
                    size={16}
                />
            )
        }
        if (contentTeamFromStudioPage) return null
        return disabled ? (
            <VisibilitySharpIcon
                className={classes.visibilityIconDisabled}
                fontSize="inherit"
                htmlColor="#AAAAAA"
                data-testid={dataTestId}
            />
        ) : isHidden ? (
            <HoverTooltip
                text={'Activate Content'}
                placements={['top', 'bottom']}
            >
                <VisibilityOffSharpIcon
                    className={
                        isArticle
                            ? classes.visibilityIconArticle
                            : classes.visibilityIcon
                    }
                    fontSize="inherit"
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    data-testid={dataTestId}
                    role="button"
                    aria-label="Activate Content"
                    tabIndex={tabIndexOverride ?? 0}
                />
            </HoverTooltip>
        ) : (
            <HoverTooltip
                text={'Inactivate Content'}
                placements={['top', 'bottom']}
            >
                <VisibilityOutlinedIcon
                    className={
                        isArticle
                            ? classes.visibilityIconArticle
                            : classes.visibilityIcon
                    }
                    fontSize="inherit"
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    data-testid={dataTestId}
                    role="button"
                    aria-label="Inactivate Content"
                    tabIndex={tabIndexOverride ?? 0}
                />
            </HoverTooltip>
        )
    }
)
